/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */
import { createSlice } from "@reduxjs/toolkit";
import { UserDto } from "../../models";
import UserState from "../../models/storeModels/UserState";

import {
  deleteTenantUser,
  exportUserListBasedOnForgeCustomerId,
  getUserListBasedOnForgeCustomerId,
  getUserExportListBasedOnForgeCustomerId,
  getUserPermissions,
} from "./user-actions";

const initialSliceState: UserState = {
  users: [],
  isLoadingIndicatorActive: false,
  errorCode: null,
  hasError: false,
  exportedData: [],
  filteredUsers: [],
  rolestoFilterTenants: [],
  selectedRolesFilter: [],
  FilterApplied: false,
  userPermissions: {
    can_authorize_user: false,
    can_create_tenant: false,
    can_create_user: false,
    can_delete_tenant: false,
    can_delete_user: false,
    can_read_all_roles: false,
    can_read_all_users: false,
    can_read_tenant: false,
    can_read_user: false,
    can_update_tenant: false,
    can_update_user: false,
    is_active_user: false,
    can_create_honeywell_user: false,
    can_read_honeywell_user: false,
    can_update_honeywell_user: false,
    can_delete_honeywell_user: false,
  },
  isUserPermissionsFetched: false,
  exportedAdminUsers: [],
};

const userSlice = createSlice({
  name: "user",
  initialState: initialSliceState,

  extraReducers(builder) {
    // get users of a customer
    builder
      .addCase(
        getUserListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.users = action.payload;
          state.isLoadingIndicatorActive = false;
        }
      )
      .addCase(getUserListBasedOnForgeCustomerId.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(getUserListBasedOnForgeCustomerId.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // get User Exported List
    builder
      .addCase(
        exportUserListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.exportedData = action.payload;
          state.isLoadingIndicatorActive = false;
        }
      )
      .addCase(
        exportUserListBasedOnForgeCustomerId.rejected,
        (state, action) => {
          state.isLoadingIndicatorActive = false;
          state.hasError = true;
          state.errorCode = setErrorCode(action);
        }
      )
      .addCase(exportUserListBasedOnForgeCustomerId.pending, (state) => {
        state.isLoadingIndicatorActive = true;
        // state.changed = true;
      });

    // delete users of a  customer
    builder
      .addCase(deleteTenantUser.fulfilled, (state, action) => {
        state.users = state.users.filter(
          (x) => !action.payload.userIds.some((y) => y === x.userId)
        );
        state.isLoadingIndicatorActive = false;
      })
      .addCase(deleteTenantUser.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(deleteTenantUser.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // get users permissions
    builder
      .addCase(getUserPermissions.fulfilled, (state, action: any) => {
        state.userPermissions = action.payload.result;
        state.isUserPermissionsFetched = true;
        state.isLoadingIndicatorActive = false;
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        state.isLoadingIndicatorActive = false;
        state.hasError = true;
        state.errorCode = setErrorCode(action);
      })
      .addCase(getUserPermissions.pending, (state) => {
        state.isLoadingIndicatorActive = true;
      });

    // get Admin Users export list

    builder
      .addCase(
        getUserExportListBasedOnForgeCustomerId.fulfilled,
        (state, action: any) => {
          state.exportedAdminUsers = action.payload;
        }
      )
      .addCase(
        getUserExportListBasedOnForgeCustomerId.rejected,
        (state, action) => {}
      )
      .addCase(getUserExportListBasedOnForgeCustomerId.pending, (state) => {});
  },
  reducers: {
    showRoleList(state, action) {
      state.rolestoFilterTenants = action.payload;
    },
    filteredRoleList(state, action) {
      state.selectedRolesFilter = action.payload.selectedValue;
      state.FilterApplied = action.payload.filterApplied;
    },
    setFilteredUserList(state, action) {
      state.filteredUsers = action.payload;
    },

    resetUserListBasedOnForgeCustomerId(state) {
      state.users = [];
    },

    // Update seelcted user roles
    updateUserRoles(state, action) {
      const userToUpdate = state.users.find(
        (x) => x.userId === action.payload.userId
      );
      const newRoles = state.rolestoFilterTenants
        .filter((x) => action.payload.roleIds.includes(x.role.roleId))
        .map((x) => x.role);

      // Update user roles if userToUpdate is present
      if (userToUpdate !== undefined) {
        userToUpdate.roles = newRoles;
        state.users = [...state.users];
      }
    },
  },
});

function setErrorCode(action: any) {
  let errorCode = action.payload as number;
  return errorCode;
}

export const userActions = userSlice.actions;

export default userSlice;
